import { memo, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { SlideUp, SpinningLoader, Text } from 'common-components'
import { useResponsive } from 'hooks/useResponsive'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { ASPECT_RATIO_VALUES, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { COLORS } from 'consts/colors'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { getAvatarAssetsForAspectRatio } from 'store/features/aiAvatar/utils'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { AvatarSource } from 'store/types/aiAvatar'
import { useAvatarUpload } from '../../hooks/useAvatarUpload'
import AspectRatioToggle from './aspect-ratio-toggle'
import * as Styled from './styled'

interface IProps {
  isOpen: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
}

const PublicAvatarsPresets: React.FC<IProps> = ({
  isOpen,
  anchorEl,
  onClose
}) => {
  const avatar = useSelector(aiAvatarSelectors.avatar)
  const publicAvatars = useSelector(aiAvatarSelectors.publicAvatarsData)
  const isLoading = useSelector(aiAvatarSelectors.isPublicAvatarsLoading)
  const avatarAspectRatio = useSelector(aiAvatarSelectors.avatarAspectRatio)
  const { aspectRatio, aspectRatioString } = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const { handleSelectPublicAvatar } = useAvatarUpload()
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()

  // The toggle's aspect ratio is panel-local (it only drives which preview
  // thumbnails / cell shape to show); it's committed to Redux only when a public
  // avatar is selected. Defaults to the project ratio.
  const [selectedAspectRatio, setSelectedAspectRatio] =
    useState(aspectRatioString)

  // Re-sync each open: a selected public avatar shows its committed ratio,
  // otherwise default to the current project ratio (avatarAspectRatio is null
  // unless a public avatar is selected).
  useEffect(() => {
    if (!isOpen) return
    setSelectedAspectRatio(avatarAspectRatio ?? aspectRatioString)
  }, [isOpen, avatarAspectRatio, aspectRatioString])

  const cellRatio = ASPECT_RATIO_VALUES[selectedAspectRatio] ?? aspectRatio

  // On mobile, hide the main sub-sidebar slider while the presets sheet is open
  // so it shows as its own slide-up (matching the Quality dropdown behavior).
  useEffect(() => {
    if (isOpen) {
      openCallback()
      return closeCallback
    }
  }, [isOpen, openCallback, closeCallback])

  const selectedId = avatar?.source === AvatarSource.PUBLIC ? avatar.id : null
  const showLoader = isLoading && !publicAvatars?.length

  const content = (
    <Styled.Panel data-testid="public-avatars-presets">
      <Styled.Header>
        <Text type={isTablet ? 'h1' : 'subtitle'} color="secondaryDark">
          {translate(T.SELECT_FROM_PUBLIC_AVATARS_LABEL)}
        </Text>
        <AspectRatioToggle
          value={selectedAspectRatio}
          onChange={setSelectedAspectRatio}
        />
      </Styled.Header>
      {showLoader ? (
        <Styled.LoaderArea>
          <SpinningLoader
            size={27}
            wrapperSize={32}
            loaderColor={COLORS.primaryDark}
            strokeWidth={2}
          />
        </Styled.LoaderArea>
      ) : (
        <Styled.ScrollArea>
          {publicAvatars?.map((preset, index) => {
            const id = String(preset.id)
            const { url, thumbnail } = getAvatarAssetsForAspectRatio(
              preset,
              selectedAspectRatio
            )
            return (
              <Styled.PresetItem
                key={id}
                $ratio={cellRatio}
                $isSelected={selectedId === id}
                onClick={() => {
                  handleSelectPublicAvatar(
                    id,
                    url,
                    preset.gender,
                    selectedAspectRatio
                  )
                  // dismiss the picker once an avatar is selected (sheet on mobile, flyout on desktop)
                  onClose()
                }}
                data-testid={`public-avatar-preset-${index}`}
              >
                {thumbnail && <Styled.PresetImage src={thumbnail} alt="" />}
              </Styled.PresetItem>
            )
          })}
        </Styled.ScrollArea>
      )}
    </Styled.Panel>
  )

  // Mobile / tablet: bottom-sheet slide-up; desktop: anchored flyout.
  // Portal to body so the sheet lives outside the main slider (which is hidden
  // via isMobileInnerSliderOpen) instead of nesting inside it.
  if (isTablet) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={onClose}>
        {content}
      </SlideUp>,
      document.body
    )
  }

  return (
    <Styled.PositionedFlyout
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      options={{
        position: 'right',
        alignmentY: 'top',
        extraOffset: 17
      }}
    >
      {content}
    </Styled.PositionedFlyout>
  )
}

export default memo(PublicAvatarsPresets)
